/**
 * Fixture CrossFrame Listeners
 *
 * Handles CrossFrame requests for fixture operations.
 * Validates required parameters and delegates to the gateway client via getServiceClient().
 */

import { getServiceClient } from '@api/client-factory/StoreManagementClientFactory'
import { ListFixturesInput } from '@amzn/coral_com-amazon-ihmvirgo-coral/ListFixturesInput'
import { GetFixtureInfoInput } from '@amzn/coral_com-amazon-ihmvirgo-coral/GetFixtureInfoInput'
import { GetFixturePlanogramInput } from '@amzn/coral_com-amazon-ihmvirgo-coral/GetFixturePlanogramInput'
import { SendFixtureScanResultsInput } from '@amzn/coral_com-amazon-ihmvirgo-coral/SendFixtureScanResultsInput'
import { GetFixtureWorkflowStatusInput } from '@amzn/coral_com-amazon-ihmvirgo-coral/GetFixtureWorkflowStatusInput'
import { ListFixtureTasksInput } from '@amzn/coral_com-amazon-ihmvirgo-coral/ListFixtureTasksInput'
import { ResetFixturesToPrebootstrapInput } from '@amzn/coral_com-amazon-ihmvirgo-coral/ResetFixturesToPrebootstrapInput'
import { StartFixtureScanInput } from '@amzn/coral_com-amazon-ihmvirgo-coral/StartFixtureScanInput'
import { AbortFixtureScanInput } from '@amzn/coral_com-amazon-ihmvirgo-coral/AbortFixtureScanInput'
import { ListFixturesOutput } from '@amzn/coral_com-amazon-ihmvirgo-coral/ListFixturesOutput'
import { GetFixtureInfoOutput } from '@amzn/coral_com-amazon-ihmvirgo-coral/GetFixtureInfoOutput'
import { GetFixturePlanogramOutput } from '@amzn/coral_com-amazon-ihmvirgo-coral/GetFixturePlanogramOutput'
import { ListFixtureTasksOutput } from '@amzn/coral_com-amazon-ihmvirgo-coral/ListFixtureTasksOutput'
import { GetFixtureWorkflowStatusOutput } from '@amzn/coral_com-amazon-ihmvirgo-coral/GetFixtureWorkflowStatusOutput'
import { validateRequired, validateRequiredArray } from './listenerUtils'

export async function listFixturesListener (message: any): Promise<ListFixturesOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId')
    const client = getServiceClient()
    return client.listFixtures(ListFixturesInput.clone(params)).toPromise()
}

export async function getFixtureInfoListener (message: any): Promise<GetFixtureInfoOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId')
    // Accept both fixtureId (deprecated) and fixtureIds; wrap single fixtureId into fixtureIds array
    if (!params.fixtureIds && params.fixtureId) {
        params.fixtureIds = [params.fixtureId]
    }
    const client = getServiceClient()
    return client.getFixtureInfo(GetFixtureInfoInput.clone(params)).toPromise()
}

export async function getFixturePlanogramListener (message: any): Promise<GetFixturePlanogramOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId')
    // Accept both fixtureId (deprecated) and fixtureIds; wrap single fixtureId into fixtureIds array
    if (!params.fixtureIds && params.fixtureId) {
        params.fixtureIds = [params.fixtureId]
    }
    const client = getServiceClient()
    return client.getFixturePlanogram(GetFixturePlanogramInput.clone(params)).toPromise()
}

export async function listFixtureTasksListener (message: any): Promise<ListFixtureTasksOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId')
    const client = getServiceClient()
    return client.listFixtureTasks(ListFixtureTasksInput.clone(params)).toPromise()
}

export async function sendFixtureScanResultsListener (message: any): Promise<void> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'fixtureId')
    const client = getServiceClient()
    // Pass full params — includes userId, volumesByScanId, volumeIdsByScanId,
    // scansByProductVolumeGroupId, fixtureScanType, startTime, endTime, fixtureLayout
    await client.sendFixtureScanResults(SendFixtureScanResultsInput.clone(params)).toPromise()
}

export async function getFixtureWorkflowStatusListener (message: any): Promise<GetFixtureWorkflowStatusOutput> {
    const params = (message && message.data) || {}
    validateRequiredArray(params, 'fixtureIds')
    validateRequired(params, 'storeId')
    const client = getServiceClient()
    return client.getFixtureWorkflowStatus(GetFixtureWorkflowStatusInput.clone(params)).toPromise()
}

export async function resetFixturesToPrebootstrapListener (message: any): Promise<void> {
    const params = (message && message.data) || {}
    validateRequiredArray(params, 'fixtureIds')
    validateRequired(params, 'storeId')
    const client = getServiceClient()
    await client.resetFixturesToPrebootstrap(ResetFixturesToPrebootstrapInput.clone(params)).toPromise()
}

export async function startFixtureScanListener (message: any): Promise<void> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'fixtureId')
    const client = getServiceClient()
    await client.startFixtureScan(StartFixtureScanInput.clone(params)).toPromise()
}

export async function abortFixtureScanListener (message: any): Promise<void> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'fixtureId')
    const client = getServiceClient()
    await client.abortFixtureScan(AbortFixtureScanInput.clone(params)).toPromise()
}
